import React, { useState } from 'react';
import { C, fmtAnnualPct, fmtNum, fmtUsd } from './shared';

// Таблиця періодів одного результату. Винесена з PeriodsPopup, бо той самий
// набір рядків потрібен ДВІЧІ: у попапі зі списку результатів і на сторінці
// результату, де вона замінила кнопки-пігулки (рішення менеджера 2026-08-17 —
// коли періодів десяток, з підписів «P7: 2023-06-01 — 2026-06-01» неможливо
// зрозуміти, який з них вартий заходу).
//
// Клікабельність опційна: у попапі рядки нічого не роблять, на сторінці
// результату рядок відкриває той самий вигляд, що раніше відкривала пігулка.

function pctColor(v) {
  return v == null ? C.textSm : v >= 0 ? C.success : C.danger;
}

function fmtSigned(v, dec = 2) {
  if (v == null) return '—';
  return (v >= 0 ? '+' : '') + Number(v).toFixed(dec) + '%';
}

// Порядок колонок за запитом менеджера (#6): Monthly перед Yearly, далі повний
// набір метрик, які ми усереднюємо/пулимо (#2.1). Усі сортовані.
export const PERIOD_COLS = [
  { key: 'period',        label: 'Period',     align: 'left',  get: p => p.start || '' },
  { key: 'days',          label: 'Days',       align: 'left',  get: p => p.days },
  { key: 'monthly_return',label: 'Monthly%',   align: 'right', fmt: v => fmtSigned(v, 2),       color: pctColor },
  { key: 'yearly_return', label: 'Yearly%',    align: 'right', fmt: v => fmtAnnualPct(v, 1),    color: pctColor },
  { key: 'win_rate',      label: 'Win%',       align: 'right', fmt: v => fmtNum(v, 1) + '%' },
  { key: 'buys',          label: 'Buys',       align: 'right', fmt: v => fmtNum(v, 0) },
  { key: 'sells',         label: 'Sells',      align: 'right', fmt: v => fmtNum(v, 0) },
  { key: 'total_return',  label: 'Total%',     align: 'right', fmt: v => fmtSigned(v, 2),       color: pctColor },
  { key: 'final_balance', label: 'Final$',     align: 'right', fmt: v => fmtUsd(v) },
  { key: 'max_drawdown',  label: 'MaxDD',      align: 'right', fmt: v => fmtNum(v, 1) + '%' },
  { key: 'sharpe',        label: 'Sharpe',     align: 'right', fmt: v => fmtNum(v, 2) },
  { key: 'calmar',        label: 'Calmar',     align: 'right', fmt: v => fmtNum(v, 2) },
  { key: 'profitable_months_pct', label: 'Profit.Mo%', align: 'right', fmt: v => fmtNum(v, 1) + '%' },
  { key: 'worst_month',   label: 'Worst Mo%',  align: 'right', fmt: v => fmtSigned(v, 2),       color: pctColor },
  // Пер-періодні витрати в $. Для рядків advanced-рушія null (ядро не віддає
  // розклад) → «—»; дохідності вище вже чисті від витрат.
  { key: 'total_commission', label: 'Comm$',   align: 'right', fmt: v => fmtUsd(v) },
  { key: 'total_spread',     label: 'Spread$', align: 'right', fmt: v => fmtUsd(v) },
];

const thStyle = {
  padding: '7px 10px', background: '#F8FAFC',
  border: '1px solid #E2E8F0', color: '#475569',
  fontWeight: 600, textAlign: 'left', whiteSpace: 'nowrap',
};

const tdStyle = {
  padding: '7px 10px', border: '1px solid #E2E8F0', whiteSpace: 'nowrap',
};

// Середнє по колонці. Саме АРИФМЕТИЧНЕ, не зважене за угодами: число під
// колонкою мусить збігатися з тим, що видно очима в самій колонці. Рядки з
// помилкою не рахуються — інакше невдалий період тягнув би середнє до нуля,
// вдаючи збиток.
// Частки вікон за знаком річної дохідності. Дзеркалить бекендний
// _aggregate_periods_full: «поганий» — СТРОГО менше нуля, нуль зараховується до
// прибуткових, тож дві частки в сумі дають рівно 100%.
function yearlySplit(rows) {
  const vals = rows
    .map(p => p.yearly_return)
    .filter(v => typeof v === 'number' && isFinite(v));
  if (!vals.length) return null;
  const neg = vals.filter(v => v < 0);
  const pos = vals.filter(v => v >= 0);
  const mean = a => (a.length ? a.reduce((x, y) => x + y, 0) / a.length : null);
  return {
    n: vals.length,
    negN: neg.length, posN: pos.length,
    negShare: (neg.length / vals.length) * 100,
    posShare: (pos.length / vals.length) * 100,
    negAvg: mean(neg), posAvg: mean(pos),
  };
}

function columnMean(rows, key) {
  const vals = rows
    .filter(p => p && !p.error)
    .map(p => p[key])
    .filter(v => typeof v === 'number' && isFinite(v));
  if (!vals.length) return null;
  return vals.reduce((a, b) => a + b, 0) / vals.length;
}

/**
 * props:
 *   periods      — масив periods_results (разом із рядком is_main)
 *   onRowClick   — (period, idx) => void; якщо не задано, рядки не клікабельні
 *   activeIdx    — індекс підсвіченого рядка (той, що зараз відкритий)
 *   rowStatus    — (idx) => 'loading' | 'error' | null; значок стану перерахунку
 *   showAverages — підсумковий рядок середніх по колонках
 *   onExtend     — (period, idx) => void; додає колонку «+» у кінці рядка
 *   extendBusy   — індекс рядка, для якого продовження зараз рахується
 */
export default function PeriodsTable({ periods, onRowClick, activeIdx = null, rowStatus,
                                       showAverages = false, onExtend, extendBusy = null }) {
  const [sortKey, setSortKey] = useState(null);   // null = хронологічний порядок
  // Скільки місяців дописати — питається ПРЯМО В РЯДКУ, а не діалогом браузера:
  // так видно, до якого саме періоду він додається (у діалозі лишається тільки
  // дата в тексті, і на сотні рядків це легко переплутати).
  const [extendRow, setExtendRow] = useState(null);
  const [extendMonths, setExtendMonths] = useState(12);
  const [sortDir, setSortDir] = useState(1);      // 1 asc, -1 desc

  const list = Array.isArray(periods) ? periods : [];

  // Підпис рахується ПО НЕ-ГОЛОВНИХ рядках, а не по позиції в масиві. Головний
  // період лежить у periods_results нульовим елементом, тож наївне `P${i+1}`
  // давало «P2» тому, що на сторінці результату звалось «P1» — два місця в UI
  // називали той самий період по-різному.
  const labels = {};
  let extraNo = 0;
  list.forEach((p, i) => { labels[i] = p && p.is_main ? '★' : `P${++extraNo}`; });

  if (!list.length) return null;

  function onSort(key) {
    if (sortKey === key) setSortDir(d => -d);
    else { setSortKey(key); setSortDir(1); }
  }

  // Сортований вигляд: зберігаємо оригінальний індекс (для «#» і для кліку),
  // рядки з помилкою завжди осідають донизу.
  const indexed = list.map((p, i) => ({ p, i }));
  if (sortKey) {
    const col = PERIOD_COLS.find(c => c.key === sortKey);
    const val = (row) => (col.get ? col.get(row.p) : row.p[sortKey]);
    indexed.sort((a, b) => {
      if (a.p.error && b.p.error) return 0;
      if (a.p.error) return 1;
      if (b.p.error) return -1;
      const av = val(a), bv = val(b);
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === 'string') return av.localeCompare(bv) * sortDir;
      return (av - bv) * sortDir;
    });
  }

  const clickable = typeof onRowClick === 'function';

  return (
    <div className="periods-table-scroll" style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, marginTop: 8 }}>
        <thead>
          <tr>
            <th style={thStyle}>#</th>
            {PERIOD_COLS.map(c => {
              const active = sortKey === c.key;
              return (
                <th
                  key={c.key}
                  onClick={() => onSort(c.key)}
                  style={{
                    ...thStyle, textAlign: c.align, cursor: 'pointer',
                    userSelect: 'none', whiteSpace: 'nowrap',
                    background: active ? '#EFF6FF' : thStyle.background,
                    color: active ? '#1E40AF' : thStyle.color,
                  }}
                  title="Сортувати"
                >
                  {c.label}{active ? (sortDir > 0 ? ' ↑' : ' ↓') : ''}
                </th>
              );
            })}
            {onExtend && <th style={{ ...thStyle, textAlign: 'center' }} title="Продовжити цей період">+</th>}
          </tr>
        </thead>
        <tbody>
          {indexed.map(({ p, i }, rowPos) => {
            const label    = labels[i];
            const isActive = activeIdx === i;
            const status   = rowStatus ? rowStatus(i) : null;
            const bg = isActive ? '#DBEAFE'
                     : p.included === false ? '#F8FAFC'
                     : p.is_main ? '#FEF9C3'
                     : (rowPos % 2 === 0 ? '#FAFAFA' : '#fff');
            const rowProps = {
              style: {
                background: bg,
                cursor: clickable ? 'pointer' : 'default',
                outline: isActive ? '2px solid #2563EB' : 'none',
                outlineOffset: '-2px',
              },
              ...(clickable ? {
                onClick: () => onRowClick(p, i),
                title: p.is_main ? 'Головний період — відкрити' : 'Відкрити цей період',
              } : {}),
            };

            if (p.error) {
              return (
                <tr key={i} {...rowProps}>
                  <td style={tdStyle}>{label}</td>
                  <td style={{ ...tdStyle, color: p.included === false ? C.textSm : undefined }}>
                    {p.start} – {p.end}{p.is_main ? ' (головний)' : ''}
                    {p.included === false && (
                      <span title={p.block_label ? `Набір «${p.block_label}» відкріплено` : 'Відкріплено від підсумку'}
                            style={{ marginLeft: 6, fontSize: 10, color: C.textSm,
                                     border: `1px solid ${C.border}`, borderRadius: 3, padding: '0 4px' }}>
                        не в підсумку
                      </span>
                    )}
                  </td>
                  <td style={tdStyle}>{p.days ?? '—'}</td>
                  <td colSpan={PERIOD_COLS.length - 2 + (onExtend ? 1 : 0)}
                      style={{ ...tdStyle, color: C.danger, fontSize: 11 }}>
                    ✗ {typeof p.error === 'string' ? p.error : (p.detail || 'error')}
                  </td>
                </tr>
              );
            }

            return (
              <tr key={i} {...rowProps}>
                <td style={{ ...tdStyle, fontWeight: 700, color: p.is_main ? '#B45309' : '#64748B' }}
                    title={p.is_main ? 'Головний період (start/stop стратегії)' : undefined}>
                  {label}
                  {status === 'loading' && <span style={{ marginLeft: 4, color: C.warning }}>⏳</span>}
                  {status === 'error'   && <span style={{ marginLeft: 4, color: C.danger }}>✗</span>}
                </td>
                {PERIOD_COLS.map(c => {
                  if (c.key === 'period') {
                    return (
                      <td key={c.key} style={tdStyle}>
                        {p.start} – {p.end}
                        {p.is_main && (
                          <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 700, color: '#B45309',
                            background: '#FEF08A', borderRadius: 4, padding: '1px 5px' }}>головний</span>
                        )}
                      </td>
                    );
                  }
                  if (c.key === 'days') {
                    return <td key={c.key} style={tdStyle}>{p.days ?? '—'}</td>;
                  }
                  const v = p[c.key];
                  let color;
                  if (c.color) color = c.color(v);
                  else if (c.key === 'win_rate') color = v != null && v >= 60 ? C.success : C.textMd;
                  return (
                    <td key={c.key} style={{ ...tdStyle, textAlign: c.align }}>
                      <span style={{ color, fontWeight: c.color ? 600 : 400 }}>
                        {v != null ? c.fmt(v) : '—'}
                      </span>
                    </td>
                  );
                })}
                {onExtend && (
                  <td style={{ ...tdStyle, textAlign: 'center' }}>
                    <button
                      type="button"
                      disabled={extendBusy != null}
                      onClick={(e) => {
                        e.stopPropagation();
                        setExtendRow(extendRow === i ? null : i);
                      }}
                      title="Додатково перевірити саме цей період, але довший"
                      style={{
                        border: `1px solid ${C.border}`, background: '#fff',
                        borderRadius: 5, width: 22, height: 22, lineHeight: '18px',
                        cursor: extendBusy != null ? 'default' : 'pointer',
                        color: extendBusy === i ? C.textSm : '#2563EB', fontWeight: 700,
                        opacity: extendBusy != null && extendBusy !== i ? 0.4 : 1,
                      }}
                    >{extendBusy === i ? '…' : extendRow === i ? '×' : '+'}</button>
                  </td>
                )}
              </tr>
            );
          })}
          {/* Форма продовження — рядком ПІД тим періодом, до якого вона
              належить: так видно, до чого саме додаються місяці. */}
          {onExtend && extendRow != null && list[extendRow] && (
            <tr key="extend-form" style={{ background: '#EFF6FF' }}>
              <td style={tdStyle} />
              <td style={tdStyle} colSpan={PERIOD_COLS.length}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 12, color: C.textMd }}>
                    Додатково перевірити саме цей період ({list[extendRow].start}) ще на
                  </span>
                  <input
                    type="number" min={1} value={extendMonths} autoFocus
                    onChange={e => setExtendMonths(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        onExtend(list[extendRow], extendRow, parseInt(extendMonths, 10));
                        setExtendRow(null);
                      }
                      if (e.key === 'Escape') setExtendRow(null);
                    }}
                    style={{ width: 62, padding: '3px 6px', fontSize: 12,
                             border: `1px solid ${C.border}`, borderRadius: 4 }}
                  />
                  <span style={{ fontSize: 12, color: C.textMd }}>місяців</span>
                  <button
                    type="button"
                    disabled={extendBusy != null}
                    onClick={() => {
                      onExtend(list[extendRow], extendRow, parseInt(extendMonths, 10));
                      setExtendRow(null);
                    }}
                    style={{ padding: '3px 12px', background: '#2563EB', color: '#fff',
                             border: 'none', borderRadius: 5, fontSize: 12, fontWeight: 600,
                             cursor: extendBusy != null ? 'default' : 'pointer' }}
                  >Перевірити</button>
                  <span style={{ fontSize: 11, color: C.textSm }}>
                    кінець = {list[extendRow].start} + {extendMonths || 0} міс.
                  </span>
                </div>
              </td>
              {onExtend && <td style={tdStyle} />}
            </tr>
          )}
        </tbody>
        {showAverages && (() => {
          // Та сама вибірка, що й на бекенді (_usable_periods): без помилок,
          // без ГОЛОВНОГО вікна (воно зміщене за побудовою — на ньому стратегію
          // й шукали) і без ВІДКРІПЛЕНИХ блоків. Розійтись цим двом місцям не
          // можна: тоді підсумок під таблицею і колонка в списку результатів
          // показували б різні числа для одного рядка.
          const ok = list.filter(p => p && !p.error && !p.is_main && p.included !== false);
          if (!ok.length) return null;
          return (
            <tfoot>
              <tr style={{ background: '#F1F5F9', fontWeight: 700 }}>
                <td style={{ ...tdStyle, fontWeight: 700 }}>Ø</td>
                {PERIOD_COLS.map(c => {
                  if (c.key === 'period') {
                    return (
                      <td key={c.key} style={{ ...tdStyle, fontWeight: 700 }}>
                        Середнє по {ok.length} {ok.length === 1 ? 'періоду' : 'періодах'}
                      </td>
                    );
                  }
                  const v = columnMean(ok, c.key === 'days' ? 'days' : c.key);
                  if (c.key === 'days') {
                    return <td key={c.key} style={{ ...tdStyle, fontWeight: 700 }}>{v != null ? Math.round(v) : '—'}</td>;
                  }
                  const color = c.color ? c.color(v) : undefined;
                  return (
                    <td key={c.key} style={{ ...tdStyle, textAlign: c.align }}>
                      <span style={{ color, fontWeight: 700 }}>{v != null ? c.fmt(v) : '—'}</span>
                    </td>
                  );
                })}
                {onExtend && <td style={tdStyle} />}
              </tr>
              {(() => {
                // Ті самі числа, що стоять у зовнішній таблиці колонками
                // «% of worst Yr» / «% of no worst Yr» та середніми в них.
                // Показані рядками під колонкою Yearly%, щоб читались саме як
                // розклад тієї колонки, а не як ще одна метрика збоку.
                const sp = yearlySplit(ok);
                if (!sp) return null;
                const line = (key, icon, label, cnt, share, avg, color) => (
                  <tr key={key} style={{ background: '#F8FAFC' }}>
                    <td style={{ ...tdStyle, fontWeight: 700, color }}>{icon}</td>
                    {PERIOD_COLS.map(c => {
                      if (c.key === 'period') {
                        return (
                          <td key={c.key} style={{ ...tdStyle, color: C.textMd }}>
                            {label}: {cnt} з {sp.n} ({share.toFixed(1)}%)
                          </td>
                        );
                      }
                      if (c.key === 'yearly_return') {
                        return (
                          <td key={c.key} style={{ ...tdStyle, textAlign: 'right' }}>
                            <span style={{ color, fontWeight: 700 }}>
                              {avg != null ? fmtAnnualPct(avg, 1) : '—'}
                            </span>
                          </td>
                        );
                      }
                      return <td key={c.key} style={tdStyle} />;
                    })}
                    {onExtend && <td style={tdStyle} />}
                  </tr>
                );
                return (
                  <>
                    {line('pos', '▲', 'Прибуткові вікна', sp.posN, sp.posShare, sp.posAvg, C.success)}
                    {line('neg', '▼', 'Збиткові вікна',  sp.negN, sp.negShare, sp.negAvg, C.danger)}
                  </>
                );
              })()}
            </tfoot>
          );
        })()}
      </table>
    </div>
  );
}
